"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAllowedDomain } from "@/lib/domains-config";

export default function Browser() {
  const [query, setQuery] = useState("");
  const [url, setUrl] = useState<string>("");
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [version, setVersion] = useState(0); // force reload key
  const [suggestions, setSuggestions] = useState<
    Array<{
      title: string;
      url: string;
      provider: string;
      snippet?: string;
      relevance?: number;
      isAllowed?: boolean;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const abortRef = useRef<AbortController | null>(null);
  const [readerHtml, setReaderHtml] = useState<string | null>(null);
  const [isReaderMode, setIsReaderMode] = useState(false);
  const [isReaderLoading, setIsReaderLoading] = useState(false);
  const [readerError, setReaderError] = useState<string | null>(null);
  const [activeUrl, setActiveUrl] = useState<string | null>(null);
  const [canEmbed, setCanEmbed] = useState<boolean | null>(null);
  const [blockedNavigation, setBlockedNavigation] = useState<string | null>(
    null
  );

  const canGo = useMemo(() => query.trim().length > 0, [query]);

  const navigate = useCallback((nextUrl: string) => {
    // Security check: Only allow navigation to approved educational domains
    if (!isAllowedDomain(nextUrl)) {
      setBlockedNavigation(nextUrl);
      // Show warning for 3 seconds
      setTimeout(() => setBlockedNavigation(null), 3000);
      return;
    }

    setUrl(nextUrl);
    setActiveUrl(nextUrl);
    // Reset reader mode for new navigation
    setIsReaderMode(false);
    setCanEmbed(null);
    setReaderHtml(null);
    setReaderError(null);
    setBlockedNavigation(null);
  }, []);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // If we have a highlighted suggestion or any suggestions, open best match
      const chosen =
        (activeIndex >= 0 && suggestions[activeIndex]) || suggestions[0];
      if (chosen?.url) {
        navigate(chosen.url);
        setShowSuggestions(false);
        return;
      }
      // If user entered a URL directly
      const raw = query.trim();
      if (/^https?:\/\//i.test(raw)) {
        navigate(raw);
      }
    },
    [query, navigate, suggestions, activeIndex]
  );

  const onReload = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  const viewerUrl = useMemo(() => {
    // Prioritize activeUrl if set, otherwise use url
    return activeUrl || url || "";
  }, [activeUrl, url]);

  // Auto-detect if we can embed and auto-switch to reader mode if needed
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const target = viewerUrl;
      if (!/^https?:\/\//i.test(target)) return;

      try {
        const res = await fetch(
          `/api/can-embed?url=${encodeURIComponent(target)}`,
          {
            cache: "no-store",
          }
        );
        const data = (await res.json()) as { canEmbed: boolean };
        if (!cancelled) {
          setCanEmbed(data.canEmbed);
          // Auto-switch to reader mode if can't embed
          if (data.canEmbed === false) {
            setIsReaderMode(true);
          }
        }
      } catch {
        // On error, assume we can try to embed
        if (!cancelled) {
          setCanEmbed(true);
        }
      }
    }
    void check();
    return () => {
      cancelled = true;
    };
  }, [viewerUrl]);

  const loadReader = useCallback(async () => {
    setReaderError(null);
    setReaderHtml(null);
    setIsReaderLoading(true);
    try {
      const target = viewerUrl;
      if (!/^https?:\/\//i.test(target)) {
        setReaderError("Enter a full URL or open a result first.");
        return;
      }
      const res = await fetch(`/api/reader?url=${encodeURIComponent(target)}`, {
        cache: "no-store",
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const errorMessage =
          errorData.message || errorData.details || `HTTP ${res.status}`;
        throw new Error(errorMessage);
      }

      const data = (await res.json()) as { html?: string; title?: string };
      setReaderHtml(data.html || "");
    } catch (error) {
      const errorMessage =
        (error as Error).message || "Unable to load this page in reader mode.";
      setReaderError(errorMessage);
    } finally {
      setIsReaderLoading(false);
    }
  }, [viewerUrl]);

  useEffect(() => {
    if (isReaderMode) {
      void loadReader();
    }
  }, [isReaderMode, loadReader]);

  // Debounced search with improved accuracy
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    const handle = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);

        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
          cache: "no-store",
        });
        if (!res.ok) throw new Error("search failed");
        const data = (await res.json()) as {
          results: Array<{
            title: string;
            url: string;
            provider: string;
            snippet?: string;
            relevance?: number;
            isAllowed?: boolean;
          }>;
          totalFound?: number;
          filtered?: number;
        };
        setSuggestions(data.results || []);
        setShowSuggestions(true);
        setActiveIndex(-1);
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") {
          setSuggestions([]);
          setShowSuggestions(false);
          setActiveIndex(-1);
        }
      } finally {
        setIsLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (!showSuggestions || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      } else if (e.key === "Enter") {
        // Handled by form submit
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    },
    [showSuggestions, suggestions.length]
  );

  const handleSuggestionClick = useCallback(
    (idx: number) => {
      const s = suggestions[idx];
      if (s) {
        navigate(s.url);
      }
    },
    [suggestions, navigate]
  );

  return (
    <div className="flex h-full w-full flex-col">
      <form
        className="p-2 border-b border-border flex items-center gap-2"
        onSubmit={onSubmit}
        onKeyDown={onKeyDown}
      >
        <div className="flex-1 min-w-[260px]">
          <input
            type="text"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            placeholder="Search educational resources…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search or URL"
          />
        </div>
        <button
          type="submit"
          className="h-9 rounded-md bg-accent px-4 text-sm text-accent-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          disabled={!canGo}
          aria-disabled={!canGo}
        >
          Go
        </button>
        <button
          type="button"
          className="h-9 rounded-md bg-surface px-3 text-sm text-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={onReload}
          aria-label="Reload"
        >
          Reload
        </button>
        {viewerUrl && (
          <button
            type="button"
            className={`h-9 rounded-md px-3 text-sm hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              isReaderMode
                ? "bg-accent text-accent-foreground"
                : "bg-surface text-foreground"
            }`}
            onClick={() => setIsReaderMode((v) => !v)}
            aria-pressed={isReaderMode}
            aria-label="Toggle reader mode"
          >
            Reader
          </button>
        )}
      </form>
      <div className="flex-1 grid grid-cols-12 gap-0 min-h-0">
        <aside className="col-span-4 border-r border-border overflow-auto">
          {suggestions.length === 0 && !isLoading ? (
            <div className="p-4 text-muted-foreground text-sm">
              No results yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {suggestions.map((s, idx) => (
                <li
                  key={`${s.url}-${idx}`}
                  className={`p-3 cursor-pointer hover:bg-muted ${
                    viewerUrl === s.url ? "bg-muted" : ""
                  }`}
                  onClick={() => handleSuggestionClick(idx)}
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-foreground/70">
                      {s.provider}
                    </span>
                    <div
                      className="font-medium truncate text-foreground"
                      title={s.title}
                    >
                      {s.title}
                    </div>
                  </div>
                  {s.snippet && (
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-1">
                      {s.snippet}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {s.url}
                  </div>
                </li>
              ))}
              {isLoading ? (
                <li className="p-3 text-xs text-muted-foreground">
                  Searching…
                </li>
              ) : null}
            </ul>
          )}
        </aside>
        <main className="col-span-8 overflow-hidden">
          <div className="no-drag relative m-2 mb-3 h-[calc(100%-2rem)] overflow-hidden rounded-lg border border-border bg-background">
            {!viewerUrl ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-sm text-muted-foreground">
                  Select a search result to view
                </div>
              </div>
            ) : isReaderMode ? (
              <div className="h-full w-full overflow-auto bg-background">
                {isReaderLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-muted-foreground animate-pulse">
                      Loading reader mode...
                    </div>
                  </div>
                ) : readerError ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-red-500">{readerError}</div>
                  </div>
                ) : readerHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: readerHtml }} />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-muted-foreground">
                      No content available
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <iframe
                  key={version}
                  ref={iframeRef}
                  src={viewerUrl}
                  className="h-full w-full"
                  title="In-app Browser"
                  sandbox="allow-scripts allow-forms allow-same-origin"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 rounded-b-lg bg-gradient-to-t from-background to-transparent" />
              </>
            )}
          </div>
          <div className="px-3 pb-2 text-xs text-foreground/60">
            {blockedNavigation ? (
              <span className="text-red-600 dark:text-red-400">
                ⚠️ Navigation blocked: Only educational sites are allowed.
              </span>
            ) : canEmbed === false ? (
              "This site blocks embedding. Using reader mode for safe viewing."
            ) : (
              "Some sites may automatically switch to reader mode if they block embedding."
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
